'use client';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  VStack,
  Text,
  Box,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';

interface ConfirmSendDialogProps {
  isOpen: boolean;
  onClose: () => void;
  cancelRef: React.RefObject<HTMLButtonElement | null>;
  onConfirm: () => void;
  isSending: boolean;
  emailTemplate: 'personal' | 'matching';
  recipientCount: number;
  channels: string[];
  header: string;
  message: string;
}

export function ConfirmSendDialog({
  isOpen,
  onClose,
  cancelRef,
  onConfirm,
  isSending,
  emailTemplate,
  recipientCount,
  channels,
  header,
  message,
}: ConfirmSendDialogProps) {
  const channelLabel =
    channels.includes('sms') && channels.includes('email')
      ? 'SMS och e-post'
      : channels.includes('sms')
        ? 'SMS'
        : 'e-post';

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Bekräfta utskick
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack align="start" spacing={3}>
              <Text>
                Du är på väg att skicka{' '}
                {emailTemplate === 'matching' ? (
                  <Text as="span" fontWeight="bold">
                    matchningsmail
                  </Text>
                ) : (
                  'ett meddelande'
                )}{' '}
                till{' '}
                <Text as="span" fontWeight="bold" color="blue.600">
                  {recipientCount} spekulanter
                </Text>{' '}
                via{' '}
                <Text as="span" fontWeight="bold">
                  {channelLabel}
                </Text>
                .
              </Text>
              {(header || message) && (
                <>
                  {header && (
                    <Box>
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color="gray.700"
                      >
                        Rubrik:
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        {header}
                      </Text>
                    </Box>
                  )}
                  {message && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        Meddelande:
                      </Text>
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        whiteSpace="pre-line"
                      >
                        {message}
                      </Text>
                    </Box>
                  )}
                </>
              )}
              {emailTemplate === 'matching' && !header && !message && (
                <Text fontSize="sm" color="gray.600">
                  De får ett mailliknande meddelande med bostadsinfo (samma mall
                  som automatiska).
                </Text>
              )}
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Meddelandet kan inte ångras efter att det har skickats.
                </Text>
              </Alert>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              variant="ghost"
              onClick={onClose}
              isDisabled={isSending}
            >
              Avbryt
            </Button>
            <Button
              colorScheme="blue"
              onClick={onConfirm}
              ml={3}
              isLoading={isSending}
              loadingText="Skickar..."
            >
              Skicka
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
